'use client';
import __useEventListener from './useEventListener';

/**
 * Custom hook for managing modal history state and actions.
 * @template T - The type of data stored in the modal state.
 * @returns An object containing the modal state and functions to open and close the modal.
 */
export function __useRouterState(onBack: () => void) {
    /**
     * Opens a modal with the given data and prevents the default behavior of the event if provided.
     * @param {T} data - The data to be displayed in the modal.
     * @param {MouseEvent<HTMLDivElement, any>} [e] - The mouse event that triggered the open function.
     * @returns None
     */
    const push = (url: string) => {
        window.history.pushState({}, '', url);
    };

    /**
     * Closes the current page by navigating back in the router and setting the state to undefined.
     * @returns None
     */
    const back = () => {
        window.history.back();
    };

    /**
     * Handles the popstate event and prevents the default behavior, stops propagation,
     * and sets the state to undefined.
     * @param {PopStateEvent} e - The popstate event object.
     * @returns None
     */
    const onPopState = (e: PopStateEvent) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        onBack();
    };

    __useEventListener('popstate', onPopState);

    return { push, back };
}
